// WbContractDetailPage — /weighbridge/pricing/contracts/:enc_id (and /new).
//
// Phase C4 Group 2. Single-page create + edit for a per-customer pricing
// contract. Multi-section form: Customer (immutable post-create) →
// Identity → Contract lines (existing records only). Delete sits behind a
// ConfirmModal on the same page.
//
// Server contract: WbContractDetailDto (see
// clients/web/docs/weighbridge/WEIGHBRIDGE_SERVER_CONTRACT.md §C4 Contract).
//
// Key constraints enforced here:
//   - `customer_enc_id` is REQUIRED on create + IMMUTABLE on edit.
//   - `cancelled` is terminal — once persisted, the lines editor is hidden
//     and the form fields are disabled (Save / Delete remain enabled so
//     callers can still rename/remove cancelled rows).
//   - Active-contract uniqueness is enforced server-side (409); we surface
//     the message inline.
//
// Customer picker: inline lightweight searchable select that loads up to
// 100 customers from /customers on mount. Different UX from the bookings
// CustomerSearch component on purpose (that one is tied to a `new customer
// mode` toggle that doesn't apply here). TODO: graduate to a debounced
// search like CustomerSearch.tsx when tenant customer counts exceed 100.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { Modal } from '../../../components/ui/Modal/Modal';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { WbCustomerSearch } from '../components/WbCustomerSearch';
import {
  useCreateContract,
  useDeleteContract,
  useReplaceContractLines,
  useUpdateContract,
  useWbContract,
  type ContractLineInput,
  type ContractLineType,
  type ContractStatus,
} from '../hooks/useWbContracts';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbContractDetailPage.module.css';

// -- Types local to this page ---------------------------------------------

interface PickerProduct {
  enc_id: string;
  name: string;
  code: string;
  default_general_public_price: number;
}

interface ProductsListResponse {
  products: PickerProduct[];
}

interface LineDraft {
  product_enc_id: string;
  product_name: string;
  product_code: string;
  type: ContractLineType;
  /** Held as string so the number input can render an empty intermediate
   *  state without flickering NaN. Coerced via Number() at submit time. */
  value: string;
}

interface FormState {
  customer_enc_id: string;
  customer_name: string;
  name: string;
  valid_from: string;
  valid_to: string;
  status: ContractStatus;
  notes: string;
}

const EMPTY_FORM: FormState = {
  customer_enc_id: '',
  customer_name: '',
  name: '',
  valid_from: '',
  valid_to: '',
  status: 'draft',
  notes: '',
};

const LINE_TYPE_OPTIONS: ReadonlyArray<{
  value: ContractLineType;
  label: string;
}> = [
  { value: 'manual', label: 'Fixed price (£)' },
  { value: 'plus_amount', label: 'Add (£)' },
  { value: 'minus_amount', label: 'Subtract (£)' },
  { value: 'plus_percent', label: 'Add (%)' },
  { value: 'minus_percent', label: 'Subtract (%)' },
];

const NEW_STATUS_OPTIONS: ReadonlyArray<{
  value: ContractStatus;
  label: string;
}> = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
];

const EDIT_STATUS_OPTIONS: ReadonlyArray<{
  value: ContractStatus;
  label: string;
}> = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
];

const TERMINAL_STATUSES: ReadonlyArray<ContractStatus> = ['cancelled', 'expired'];

function previewLine(type: ContractLineType, value: number): string {
  const safe = Number.isFinite(value) ? value : 0;
  const fixed = safe.toFixed(2);
  switch (type) {
    case 'manual':
      return `£${fixed}`;
    case 'plus_amount':
      return `Default + £${fixed}`;
    case 'minus_amount':
      // U+2212 minus sign for typographic correctness.
      return `Default − £${fixed}`;
    case 'plus_percent':
      return `Default + ${safe}%`;
    case 'minus_percent':
      return `Default − ${safe}%`;
    default:
      return `${value}`;
  }
}

export function WbContractDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { enc_id } = useParams<{ enc_id: string }>();
  const isNew = enc_id === 'new' || enc_id === undefined;

  const contractQuery = useWbContract(isNew ? undefined : enc_id);
  const createMut = useCreateContract();
  const updateMut = useUpdateContract(isNew ? '' : (enc_id as string));
  const deleteMut = useDeleteContract(isNew ? '' : (enc_id as string));
  const linesMut = useReplaceContractLines(isNew ? '' : (enc_id as string));

  // Form state ----------------------------------------------------------
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [productPickerOpen, setProductPickerOpen] = useState(false);

  // Hydrate the form once the GET resolves.
  useEffect(() => {
    if (isNew) return;
    const c = contractQuery.data?.contract;
    if (!c) return;
    setForm({
      customer_enc_id: c.customer.enc_id,
      customer_name: c.customer.name,
      name: c.name,
      valid_from: c.valid_from,
      valid_to: c.valid_to,
      status: c.status,
      notes: c.notes ?? '',
    });
    setLines(
      c.lines.map((l) => ({
        product_enc_id: l.product.enc_id,
        product_name: l.product.name,
        product_code: l.product.code,
        type: l.type,
        value: String(l.value),
      })),
    );
  }, [isNew, contractQuery.data]);

  // The persisted status drives whether we treat the page as "locked"
  // (cancelled record). The in-form `status` may temporarily diverge while
  // the user picks a new value before saving.
  const persistedStatus: ContractStatus | null = isNew
    ? null
    : contractQuery.data?.contract.status ?? null;
  const isPersistedCancelled = persistedStatus === 'cancelled';
  const isPersistedTerminal = persistedStatus !== null
    ? TERMINAL_STATUSES.includes(persistedStatus)
    : false;

  // -- Validation -------------------------------------------------------

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (isNew && form.customer_enc_id.trim() === '') {
      errs['customer_enc_id'] = 'Customer is required.';
    }
    if (!form.name.trim()) errs['name'] = 'Name is required.';
    if (form.name.length > 200) {
      errs['name'] = 'Name must be 200 characters or fewer.';
    }
    if (!form.valid_from.trim()) {
      errs['valid_from'] = 'Valid from is required.';
    }
    if (!form.valid_to.trim()) {
      errs['valid_to'] = 'Valid to is required.';
    }
    if (
      form.valid_from.trim() !== '' &&
      form.valid_to.trim() !== '' &&
      !(form.valid_to > form.valid_from)
    ) {
      errs['valid_to'] = 'valid_to must be after valid_from.';
    }
    if (form.notes.length > 5000) {
      errs['notes'] = 'Notes must be 5000 characters or fewer.';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  // -- Submit -----------------------------------------------------------

  async function performSave(): Promise<void> {
    if (isNew) {
      await new Promise<void>((resolve, reject) => {
        createMut.mutate(
          {
            customer_enc_id: form.customer_enc_id,
            name: form.name.trim(),
            valid_from: form.valid_from,
            valid_to: form.valid_to,
            status: form.status,
            notes: form.notes.trim() === '' ? null : form.notes.trim(),
          },
          {
            onSuccess: () => {
              navigate('/weighbridge/pricing/contracts');
              resolve();
            },
            onError: (err) => {
              setError(extractErrorMessage(err));
              reject(err);
            },
          },
        );
      });
      return;
    }

    // Edit path: PATCH the contract (NO customer_enc_id), then PUT the
    // lines unless we're in a terminal state.
    await new Promise<void>((resolve, reject) => {
      updateMut.mutate(
        {
          name: form.name.trim(),
          valid_from: form.valid_from,
          valid_to: form.valid_to,
          status: form.status,
          notes: form.notes.trim() === '' ? null : form.notes.trim(),
        },
        {
          onSuccess: () => resolve(),
          onError: (err) => {
            setError(extractErrorMessage(err));
            reject(err);
          },
        },
      );
    });

    if (!TERMINAL_STATUSES.includes(form.status)) {
      await new Promise<void>((resolve, reject) => {
        linesMut.mutate(
          {
            lines: lines.map<ContractLineInput>((l) => ({
              product_enc_id: l.product_enc_id,
              type: l.type,
              value: Number(l.value === '' ? 0 : l.value),
            })),
          },
          {
            onSuccess: () => resolve(),
            onError: (err) => {
              // PATCH succeeded but PUT lines failed — surface the message
              // and do NOT navigate so the user can retry.
              setError(extractErrorMessage(err));
              reject(err);
            },
          },
        );
      });
    }

    navigate('/weighbridge/pricing/contracts');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!validate()) return;

    // Cancel-status guard — only kick the modal when the user is
    // transitioning a NON-cancelled record into 'cancelled'. Once
    // persisted as cancelled, the select is disabled anyway.
    const movingToCancelled =
      !isNew &&
      form.status === 'cancelled' &&
      persistedStatus !== null &&
      persistedStatus !== 'cancelled';
    if (movingToCancelled) {
      setConfirmCancelOpen(true);
      return;
    }

    void performSave();
  }

  function handleCancelConfirm() {
    setConfirmCancelOpen(false);
    void performSave();
  }

  function handleCancelAbort() {
    setConfirmCancelOpen(false);
    // Roll the select back to the persisted status.
    if (persistedStatus !== null) {
      setForm((prev) => ({ ...prev, status: persistedStatus }));
    }
  }

  function handleDeleteConfirm() {
    deleteMut.mutate(undefined, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
        navigate('/weighbridge/pricing/contracts');
      },
      onError: (err) => {
        setConfirmDeleteOpen(false);
        setError(extractErrorMessage(err));
      },
    });
  }

  // -- Line editor helpers ---------------------------------------------

  function removeLine(productEncId: string) {
    setLines((prev) =>
      prev.filter((l) => l.product_enc_id !== productEncId),
    );
  }

  function updateLine(productEncId: string, patch: Partial<LineDraft>) {
    setLines((prev) =>
      prev.map((l) =>
        l.product_enc_id === productEncId ? { ...l, ...patch } : l,
      ),
    );
  }

  function addLineFromProduct(p: PickerProduct) {
    setLines((prev) => {
      if (prev.some((l) => l.product_enc_id === p.enc_id)) return prev;
      return [
        ...prev,
        {
          product_enc_id: p.enc_id,
          product_name: p.name,
          product_code: p.code,
          type: 'manual',
          value: String(p.default_general_public_price),
        },
      ];
    });
    setProductPickerOpen(false);
  }

  const isSaving =
    createMut.isPending || updateMut.isPending || linesMut.isPending;
  const isLoadingExisting = !isNew && contractQuery.isLoading;

  // Disable the form fields when the contract is already cancelled. Save
  // / Delete stay enabled per the spec (you can still rename or remove a
  // cancelled row).
  const formDisabled = isPersistedCancelled;

  return (
    <div className={styles.page}>
      <PageHeader
        title={isNew ? 'New Contract' : form.name || 'Edit Contract'}
        subtitle={
          isNew
            ? 'Per-customer pricing override'
            : `${form.customer_name} · ${form.valid_from} → ${form.valid_to}`
        }
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/weighbridge/pricing/contracts')}
          >
            Back to Contracts
          </Button>
        }
      />

      {isLoadingExisting ? (
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading contract" />
          </div>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          {isPersistedCancelled ? (
            <div className={styles.cancelledBanner} role="status">
              This contract has been cancelled.
            </div>
          ) : null}

          {/* -- Section 1: Customer ----------------------------------- */}
          <Card>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Customer</h3>
              {isNew ? (
                <label className={styles.field}>
                  <span>Customer</span>
                  <WbCustomerSearch
                    value={form.customer_enc_id || null}
                    onChange={(encId, name) =>
                      setForm((prev) => ({
                        ...prev,
                        customer_enc_id: encId ?? '',
                        customer_name: name ?? '',
                      }))
                    }
                    required
                  />
                  {fieldErrors['customer_enc_id'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['customer_enc_id']}
                    </span>
                  ) : null}
                  <span className={styles.fieldHelp}>
                    Customer is immutable after the contract is created.
                  </span>
                </label>
              ) : (
                <>
                  <p className={styles.readOnlyValue}>
                    <span className={styles.readOnlyLabel}>Customer:</span>{' '}
                    <strong>{form.customer_name}</strong>
                  </p>
                  <p className={styles.fieldHelp}>
                    Customer is immutable. To reassign, cancel and create a
                    new contract.
                  </p>
                </>
              )}
            </div>
          </Card>

          {/* -- Section 2: Identity ----------------------------------- */}
          <Card>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Identity</h3>
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span>Name</span>
                  <input
                    type="text"
                    required
                    maxLength={200}
                    value={form.name}
                    disabled={formDisabled}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                  />
                  {fieldErrors['name'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['name']}
                    </span>
                  ) : null}
                </label>

                <label className={styles.field}>
                  <span>Status</span>
                  <select
                    value={form.status}
                    disabled={formDisabled}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        status: e.target.value as ContractStatus,
                      }))
                    }
                  >
                    {(isNew ? NEW_STATUS_OPTIONS : EDIT_STATUS_OPTIONS).map(
                      (s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ),
                    )}
                  </select>
                </label>

                <label className={styles.field}>
                  <span>Valid from</span>
                  <input
                    type="date"
                    required
                    value={form.valid_from}
                    disabled={formDisabled}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        valid_from: e.target.value,
                      }))
                    }
                  />
                  {fieldErrors['valid_from'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['valid_from']}
                    </span>
                  ) : null}
                </label>

                <label className={styles.field}>
                  <span>Valid to</span>
                  <input
                    type="date"
                    required
                    value={form.valid_to}
                    disabled={formDisabled}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        valid_to: e.target.value,
                      }))
                    }
                  />
                  {fieldErrors['valid_to'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['valid_to']}
                    </span>
                  ) : null}
                </label>
              </div>

              <label className={styles.field}>
                <span>Notes</span>
                <textarea
                  rows={3}
                  maxLength={5000}
                  value={form.notes}
                  disabled={formDisabled}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Optional"
                />
                {fieldErrors['notes'] ? (
                  <span className={styles.fieldError}>
                    {fieldErrors['notes']}
                  </span>
                ) : null}
              </label>
            </div>
          </Card>

          {/* -- Section 3: Contract lines ----------------------------- */}
          {!isNew ? (
            <Card>
              <div className={styles.section}>
                <div className={styles.linesHeader}>
                  <h3 className={styles.sectionTitle}>Contract lines</h3>
                  {!isPersistedTerminal ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      icon={Plus}
                      onClick={() => setProductPickerOpen(true)}
                    >
                      Add product
                    </Button>
                  ) : null}
                </div>

                {isPersistedTerminal ? (
                  <p className={styles.fieldHelp}>
                    Line items are read-only for{' '}
                    {persistedStatus === 'cancelled'
                      ? 'cancelled'
                      : 'expired'}{' '}
                    contracts.
                  </p>
                ) : lines.length === 0 ? (
                  <p className={styles.fieldHelp}>
                    No lines yet — add a product to start overriding prices.
                  </p>
                ) : (
                  <table className={styles.linesTable}>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Pricing type</th>
                        <th className={styles.numCell}>Value</th>
                        <th>Preview</th>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l) => {
                        const isPercent =
                          l.type === 'plus_percent' ||
                          l.type === 'minus_percent';
                        return (
                          <tr key={l.product_enc_id}>
                            <td>
                              <div className={styles.productName}>
                                {l.product_name}
                              </div>
                              <div className={styles.productCode}>
                                {l.product_code}
                              </div>
                            </td>
                            <td>
                              <select
                                value={l.type}
                                onChange={(e) =>
                                  updateLine(l.product_enc_id, {
                                    type: e.target
                                      .value as ContractLineType,
                                  })
                                }
                              >
                                {LINE_TYPE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className={styles.numCell}>
                              <input
                                type="number"
                                min={0}
                                step={isPercent ? '0.1' : '0.01'}
                                value={l.value}
                                onChange={(e) =>
                                  updateLine(l.product_enc_id, {
                                    value: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              {previewLine(
                                l.type,
                                Number(l.value === '' ? 0 : l.value),
                              )}
                            </td>
                            <td className={styles.actionsCell}>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                icon={Trash2}
                                aria-label={`Remove ${l.product_name}`}
                                onClick={() =>
                                  removeLine(l.product_enc_id)
                                }
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </Card>
          ) : null}

          {error ? <p className={styles.error}>{error}</p> : null}

          <div className={styles.actions}>
            <div>
              {!isNew ? (
                <Button
                  type="button"
                  variant="danger"
                  icon={Trash2}
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={isSaving}
                >
                  Delete
                </Button>
              ) : null}
            </div>
            <div className={styles.rightActions}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/weighbridge/pricing/contracts')}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={isSaving}>
                {isNew ? 'Create Contract' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>
      )}

      <ConfirmModal
        open={confirmDeleteOpen}
        title="Delete contract?"
        message={
          <p>
            Delete <strong>{form.name || 'this contract'}</strong>? This
            cannot be undone.
          </p>
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDeleteOpen(false)}
        loading={deleteMut.isPending}
      />

      <ConfirmModal
        open={confirmCancelOpen}
        title="Cancel this contract?"
        message={
          <p>
            Cancelling this contract is permanent. The contract cannot be
            reactivated. Continue?
          </p>
        }
        variant="danger"
        confirmLabel="Cancel contract"
        cancelLabel="Keep editing"
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelAbort}
      />

      <ProductPickerModal
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        existingProductEncIds={lines.map((l) => l.product_enc_id)}
        onPick={addLineFromProduct}
      />
    </div>
  );
}

// -- Product picker modal --------------------------------------------------

interface ProductPickerModalProps {
  open: boolean;
  onClose: () => void;
  existingProductEncIds: string[];
  onPick: (product: PickerProduct) => void;
}

function ProductPickerModal({
  open,
  onClose,
  existingProductEncIds,
  onPick,
}: ProductPickerModalProps): JSX.Element {
  // Up to 100 active products. Mirrors the customer-picker approach: a
  // simple eager load for tenants with small catalogues, with the option
  // to switch to debounced search later.
  const productsQuery = useQuery<ProductsListResponse>({
    queryKey: queryKeys.wb.products({ per_page: 100, is_active: true }),
    queryFn: async () => {
      const res = await apiClient.get<ProductsListResponse>(
        endpoints.wb.products,
        { params: { per_page: 100, is_active: true } },
      );
      return res.data;
    },
    enabled: open,
    staleTime: 60_000,
  });

  const [selected, setSelected] = useState<string>('');

  const products = productsQuery.data?.products ?? [];
  const available = products.filter(
    (p) => !existingProductEncIds.includes(p.enc_id),
  );

  function handleAdd() {
    const picked = available.find((p) => p.enc_id === selected);
    if (!picked) return;
    onPick(picked);
    setSelected('');
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add product"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAdd}
            disabled={selected === ''}
          >
            Add
          </Button>
        </>
      }
    >
      {productsQuery.isLoading ? (
        <Spinner label="Loading products" />
      ) : available.length === 0 ? (
        <p>No more products available to add.</p>
      ) : (
        <label className={styles.field}>
          <span>Product</span>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            aria-label="Product to add"
          >
            <option value="">— Select a product —</option>
            {available.map((p) => (
              <option key={p.enc_id} value={p.enc_id}>
                {p.name} ({p.code})
              </option>
            ))}
          </select>
        </label>
      )}
    </Modal>
  );
}
